import { AlertTriangle, CheckCircle2, Info, X } from 'lucide-react';
import { useEffect, useState } from 'react';

import { cn } from '@/shared/lib/cn';

export type ToastTone = 'success' | 'error' | 'info';

export interface ToastInput {
  tone: ToastTone;
  title: string;
  body?: string;
  duration_ms?: number;
}

interface ToastItem extends ToastInput {
  id: string;
  created_at: number;
}

type ToastListener = (items: ToastItem[]) => void;

let toast_items: ToastItem[] = [];
const listeners = new Set<ToastListener>();

export function showToast(input: ToastInput): string {
  const now = Date.now();
  const duplicate = toast_items.find(
    (item) =>
      item.tone === input.tone &&
      item.title === input.title &&
      item.body === input.body &&
      now - item.created_at < 1200,
  );
  if (duplicate) return duplicate.id;

  const id =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const item: ToastItem = { ...input, id, created_at: now };
  toast_items = [...toast_items, item].slice(-4);
  emitToasts();

  window.setTimeout(() => dismissToast(id), input.duration_ms ?? 4500);
  return id;
}

export function dismissToast(id: string): void {
  toast_items = toast_items.filter((item) => item.id !== id);
  emitToasts();
}

export function ToastViewport(): JSX.Element {
  const [items, setItems] = useState<ToastItem[]>(toast_items);

  useEffect(() => {
    listeners.add(setItems);
    return () => {
      listeners.delete(setItems);
    };
  }, []);

  return (
    <div
      aria-live="polite"
      aria-atomic="true"
      className="fixed right-4 top-4 z-[70] flex w-[min(24rem,calc(100vw-2rem))] flex-col gap-2"
    >
      {items.map((item) => (
        <ToastCard key={item.id} item={item} />
      ))}
    </div>
  );
}

function ToastCard({ item }: { item: ToastItem }): JSX.Element {
  const Icon = item.tone === 'success' ? CheckCircle2 : item.tone === 'error' ? AlertTriangle : Info;

  return (
    <div
      role={item.tone === 'error' ? 'alert' : 'status'}
      className={cn(
        'flex items-start gap-3 rounded-md border p-3 text-sm shadow-lg',
        item.tone === 'success' &&
          'border-emerald-500/40 bg-emerald-50 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-100',
        item.tone === 'error' && 'border-destructive/40 bg-destructive/10 text-destructive',
        item.tone === 'info' && 'border-primary/30 bg-primary/10 text-foreground',
      )}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="font-semibold">{item.title}</p>
        {item.body ? <p className="mt-0.5 opacity-90">{item.body}</p> : null}
      </div>
      <button
        type="button"
        onClick={() => dismissToast(item.id)}
        className="rounded-sm p-1 opacity-70 hover:opacity-100"
        aria-label="Cerrar alerta"
      >
        <X className="h-4 w-4" aria-hidden />
      </button>
    </div>
  );
}

function emitToasts(): void {
  listeners.forEach((listener) => listener(toast_items));
}
